// Server shape -> UI shape.
//
// The components and their tests are built around the shapes that used to come
// from src/data/*.js. The API speaks a different dialect (topicId/topicName/
// subject vs id/title/subj), so every difference is absorbed here. If a server
// field name ever leaks past this file into a component, this file is wrong.

import { isoToDmy, isoToMdy } from '../utils/dates';

// --- primitives --------------------------------------------------------------

// The API validates subjects as 'biology' | 'physics' | 'chemistry'
// (middleware/topic_middleware.js: checkSubject); the UI has always used the
// short forms, which are also the CSS class suffixes (.sub-bio-tint).
const SUBJECT_TO_UI = { biology: 'bio', chemistry: 'chem', physics: 'phys' };
const SUBJECT_TO_API = { bio: 'Biology', chem: 'Chemistry', phys: 'Physics' };

export const subjectToUi = (subject) =>
  SUBJECT_TO_UI[String(subject || '').toLowerCase()] || 'bio';

export const subjectToApi = (subj) => SUBJECT_TO_API[subj] || 'Biology';

// The API validates semesters as 'jun' | 'nov' (checkSemester). Keep the raw
// value as the form value and label it for humans.
export const SEMESTER_OPTIONS = [
  { value: 'Jun', label: 'June' },
  { value: 'Nov', label: 'November' },
];

// A server timestamp ('2026-08-15T00:00:00.000Z') to the d/m/yyyy the UI
// prints. isoToDmy only understands a bare date, so trim the time first.
export const serverDateToDmy = (value) => {
  if (!value) return '';
  const datePart = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return '';
  return isoToDmy(datePart);
};

/**
 * Classroom content is one `material` table server-side, split in the UI into
 * PDFs and recorded videos.
 *
 * The server's own `type` is deliberately IGNORED, because the two endpoints
 * that send one disagree with each other:
 *   getAllMaterials  (material_controller.js) — `document` ends in '.pdf'
 *   get_topic_by_id  (topic_controller.js)    — whether `document` vs `link` is set
 * Uploaded files and pasted links both go into `document` (the `link` column is
 * never written), so the second rule labels every YouTube link 'pdf' and would
 * hand it to the PDF viewer. The extension is the only rule that matches how
 * the data is actually stored, so it is applied here to every material no
 * matter which endpoint produced it.
 */
export const materialType = (material) => {
  const doc = String(material?.document || material?.link || '');
  return doc.slice(-4).toLowerCase() === '.pdf' ? 'pdf' : 'url';
};

// --- users -------------------------------------------------------------------

// POST /login and GET /login/me. Login echoes `email`; /me does not, so the
// caller passes through whatever it already knows.
export const toUiUser = (data, fallback = {}) => ({
  id: String(data?.id ?? fallback.id ?? ''),
  name: data?.name || fallback.name || '',
  email: data?.email || fallback.email || '',
  // The JWT's `type` is the real authority, but the payload carries `role`:
  // 'student' for students, and for admins an authority level (owner vs
  // assistant). Anything that is not 'student' is staff.
  role: data?.role === 'student' ? 'student' : 'admin',
  adminRole: data?.role === 'student' ? null : data?.role || null,
  group: data?.group || fallback.group || '',
  assistantId: data?.assistantId ?? fallback.assistantId ?? null,
});

// --- classroom ---------------------------------------------------------------

/**
 * A topic row plus, optionally, its materials.
 *
 * The UI treats a topic as a container for materials / videos / sessions /
 * quizzes / assignments; the server keeps those in separate tables. The nested
 * arrays are ALWAYS present (pages do `topic.materials.length` unguarded) and
 * default to empty, so a topic renders before its contents have loaded.
 */
export const toUiTopic = (topic, materials = []) => {
  const items = materials.map(toUiMaterial);
  const count = (v) => (v == null || v === '' ? null : Number(v));
  return {
    // getAllTopics returns raw rows keyed `topicId`; get_topic_by_id returns a
    // hand-built object keyed `id`. Accept either.
    id: String(topic?.topicId ?? topic?.id ?? ''),
    title: topic?.topicName || '',
    subj: subjectToUi(topic?.subject),
    // No description column on `topic` — the UI renders an empty line.
    blurb: topic?.description || '',
    semester: topic?.semester || '',
    group: topic?.group || '',
    /**
     * How much is in the topic, so a card can say so without fetching its
     * contents. Null when the response doesn't carry them (get_topic_by_id
     * doesn't), so a caller can tell "none" from "not told".
     *
     * `materialsCount` is the list endpoint's own total, which counts EVERY
     * material row — PDFs and pasted links together. The card wants them apart
     * (the topic page lists "Course materials" and "Recorded sessions"
     * separately), so listTopics refines it and fills in `videosCount`; the
     * server's figure stands as the fallback.
     *
     * The server's `sessionsCount` is a different thing again — the topic's LIVE
     * classes, from the session table — so it is kept under its own name rather
     * than confused with the recorded ones.
     */
    materialsCount: count(topic?.materialsCount),
    videosCount: null,
    liveSessionsCount: count(topic?.sessionsCount),
    materials: items.filter((m) => m.kind === 'pdf'),
    videos: items.filter((m) => m.kind === 'url'),
    sessions: [],
    quizzes: [],
    assignments: [],
  };
};

/**
 * One material row in whichever of the two UI shapes it belongs to.
 * `kind` is stripped by toUiTopic once it has done the splitting; both shapes
 * keep it so a caller that doesn't split can still tell them apart.
 *   pdf -> { id, title, pages, file }   (material card + PdfViewer)
 *   url -> { id, title, dur, n, url }   (lesson card, links out)
 */
export const toUiMaterial = (material) => {
  const kind = materialType(material);
  const base = {
    kind,
    id: String(material?.materialId ?? ''),
    title: material?.title || '',
    topicId: String(material?.topicId ?? ''),
  };
  if (kind === 'pdf') {
    return { ...base, pages: material?.description || '', file: material?.document || '' };
  }
  // Recorded sessions: the server has no duration or lesson number, and the
  // lesson card already renders those conditionally.
  return { ...base, dur: null, n: material?.description || null, url: material?.document || '' };
};

/**
 * UI form -> server body, for BOTH creating and updating a material.
 *
 * The two UI forms speak different field names for the same two columns:
 *   PDF material    { title, pages, file }
 *   Recorded session{ title, n,     url  }
 * and the server has one shape: { title, description, document, topicId }.
 * Both an uploaded R2 URL and a pasted link go into `document` — the `link`
 * column is vestigial and never written.
 *
 * `topicId` is included on updates too, not just creates: the update route runs
 * the same `checkInputData` middleware, which rejects the request outright
 * unless BOTH title and topicId are present.
 */
export const materialToApi = ({ title, pages, n, description, file, url, document, topicId }) => ({
  title: String(title || '').trim(),
  description: String(pages ?? n ?? description ?? '').trim(),
  document: file || url || document || '',
  topicId: Number(topicId),
});

// UI -> server for the topic form.
export const topicToApi = ({ title, subj, semester, group }) => ({
  topicName: String(title || '').trim(),
  subject: subjectToApi(subj),
  semester,
  ...(group ? { group } : {}),
});

// --- coursework --------------------------------------------------------------

// The server computes a per-student `state` on each assignment
// (assignment_controller.js); the UI's chips key off its own three names
// (utils/status.js).
const STATE_TO_STATUS = { submitted: 'completed', missing: 'missing', unsubmitted: 'pending' };

/**
 * One assignment as the student's Coursework page reads it.
 *
 *   { assignId, title, description, mark, document, startDate, endDate,
 *     semester, topicId, group, subject, state }
 * becomes
 *   { id, title, due, status, subj, max, file, … }
 *
 * `file` is the question paper. The student's own submission and the marked copy
 * live on a different endpoint (showMySubmission), so they are merged in by
 * `mergeSubmissions` rather than guessed at here.
 */
export const toUiAssignment = (a) => ({
  id: String(a?.assignId ?? a?.id ?? ''),
  title: a?.title || '',
  description: a?.description || '',
  due: serverDateToDmy(a?.endDate),
  status: STATE_TO_STATUS[a?.state] || 'pending',
  subj: subjectToUi(a?.subject),
  max: a?.mark ?? null,
  file: a?.document || null,
  topicId: String(a?.topicId ?? ''),
  semester: a?.semester || '',
});

/**
 * One quiz for the same page. Quizzes have a duration rather than a due date,
 * and the UI shows `date` + `length`.
 *
 * The two coursework endpoints report "has this student handed it in?" in
 * different currencies: an assignment carries the string `state`, a quiz carries
 * a BOOLEAN `submitted`. Reading only `state` here meant a submitted quiz stayed
 * 'upcoming' forever. Both are honoured, boolean first, and the vocabularies are
 * kept apart rather than merged.
 */
export const toUiQuiz = (q) => ({
  id: String(q?.quizId ?? q?.id ?? ''),
  title: q?.title || '',
  description: q?.description || '',
  // getAllQuizzes carries no subject of its own — it comes from the topic.
  subj: subjectToUi(q?.subject),
  // No endDate on a quiz: the sitting date is `date`.
  date: serverDateToDmy(q?.date ?? q?.startDate),
  length: q?.durationInMin ? `${q.durationInMin} min` : '',
  status: q?.submitted === true ? 'completed' : STATE_TO_STATUS[q?.state] || 'upcoming',
  submitted: q?.submitted === true,
  max: q?.mark ?? null,
  topicId: String(q?.topicId ?? ''),
});

/**
 * UI form -> POST /assignment/createAssignment.
 *   body: { mark, document, endDate, semester, topicId, title, description }
 * `startDate` and `publisher` are set server-side from the clock and the token,
 * and `subject` is inherited from the topic — none of them are sent.
 *
 * `dueIso` is the raw <input type="date"> value; the server parses the slashed
 * date as month-first (isoToMdy).
 *
 * `document` must be a URL ending in '.pdf' — the server validates the extension
 * and rejects a missing or empty value with "the pdf link must end with .pdf",
 * which is why the form requires a paper before it will submit.
 */
export const assignmentToApi = ({ title, description, max, semester, dueIso, topicId, paper }) => ({
  title: String(title || '').trim(),
  description: String(description || '').trim(),
  mark: Number(max),
  semester,
  endDate: isoToMdy(dueIso),
  topicId: Number(topicId),
  document: paper || '',
});

/**
 * UI form -> POST /quiz/createQuiz.
 *   body: { mark, date, semester, durationInMin, topicId, title }
 *
 * The two create endpoints are NOT symmetric, and not just in their date field:
 * a quiz has NO DOCUMENT AT ALL. There is nowhere to put a paper — neither
 * createQuiz nor getAllQuizzes nor get_quiz_by_id mentions one — so the form
 * doesn't ask for one. A quiz is a mark, a date and a duration; students sit it
 * live (startQuiz / submitActiveQuiz) rather than downloading it.
 */
export const quizToApi = ({ title, max, semester, dueIso, topicId, duration }) => ({
  title: String(title || '').trim(),
  mark: Number(max),
  semester,
  date: isoToMdy(dueIso),
  durationInMin: Number(duration),
  topicId: Number(topicId),
});

/**
 * The student's own submissions, keyed for merging.
 *   { id, type, name, subject, quizId, assignmentId, submittedAt, score }
 * The row carries no URL for the handed-in file or the marked copy — those come
 * from showSubmission/:id and showMarkedSubmission/:id, fetched on demand when
 * the student opens one.
 */
export const toUiSubmission = (s) => ({
  id: String(s?.id ?? ''),
  type: s?.type === 'quiz' ? 'quiz' : 'assignment',
  title: s?.name || '',
  subj: subjectToUi(s?.subject),
  itemId: String(s?.type === 'quiz' ? (s?.quizId ?? '') : (s?.assignmentId ?? '')),
  submittedAt: serverDateToDmy(s?.submittedAt),
  grade: s?.score ?? null,
});

// --- feed --------------------------------------------------------------------

/**
 * One announcement. GET /feed answers with `data` as a BARE ARRAY of
 *   { id, text, semester, adminId, createdAt }
 * and 404s "Feed is empty" when there are none.
 *
 * There is no title and NO AUTHOR NAME — only `adminId` — so the card shows the
 * semester it was aimed at and leaves naming the author to the page.
 */
export const toUiFeedPost = (p) => ({
  id: String(p?.id ?? p?.feedId ?? ''),
  body: p?.text || '',
  at: serverDateToDmy(p?.createdAt ?? p?.dateAndTime),
  semester: p?.semester || '',
  adminId: p?.adminId == null ? null : String(p.adminId),
});

// --- weekly report -----------------------------------------------------------

// The report's own status words, mapped to the UI's chip vocabulary. All four the
// server can send are listed: 'Unsubmitted (Still Open)' is not yet late, so it
// is pending rather than missing.
const REPORT_STATUS = {
  Marked: 'completed',
  'Pending Review': 'pending',
  'Unsubmitted (Still Open)': 'pending',
  Missing: 'missing',
};
const SESSION_STATUS = { Attended: 'attended', Late: 'late', Missed: 'missed' };

/**
 * One topic's weekly report, for the student's Report page.
 *
 * Shape (getMyWeeklyReport, since the multi-quiz change):
 *   { id, topicTitle, studentName, semester,
 *     totalSessions, sessionsAttended, totalAssignments, totalQuizzes,
 *     submittedAssignments,
 *     assignments: [{ id, title, fullMark, scoredMark, date, status,
 *                     submissionId, hasMarkedPdf, feedback }],
 *     quizzes:     [{ id, title, fullMark, scoredMark, percentage, grade,
 *                     status, submissionId, hasMarkedPdf, feedback }],
 *     sessions:    [{ sessionId, date, status }] }
 *
 * The older shape is still read, in case a server hasn't been updated: the
 * assignments came as `materials` with `maxPoints` / `score`, and only ONE quiz
 * survived, as `quizData` plus a top-level `quizGrade`.
 *
 * The assignments are renamed to `items` here. Unknown values arrive as the
 * string "N/A" and are normalised to null rather than rendered as text in a
 * number slot.
 */
export const toUiReport = (r) => {
  if (!r) return null;
  const na = (v) => (v === 'N/A' || v === undefined ? null : v);
  /**
   * The report mixes numbers with numeric STRINGS — `score` and `maxPoints` come
   * back as '44' / '50' from some rows and 44 / 50 from others — so a value counts
   * as a number whenever it reads as one. Requiring `typeof v === 'number'` threw
   * away every string score: the student's mark vanished from the report, and
   * anything keyed off "is there a score?" concluded the work was unmarked.
   * 'N/A' and '' still become null, because Number('N/A') is NaN.
   */
  const num = (v) => {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const assignmentRows = Array.isArray(r.assignments) ? r.assignments
    : Array.isArray(r.materials) ? r.materials : [];
  const items = assignmentRows.map((m) => ({
    id: String(m?.id ?? ''),
    title: m?.title || '',
    max: num(m?.fullMark ?? m?.maxPoints),
    date: serverDateToDmy(m?.date),
    status: REPORT_STATUS[m?.status] || 'pending',
    score: num(na(m?.scoredMark ?? m?.score)),
    feedback: na(m?.feedback) || '',
    submissionId: m?.submissionId == null ? null : String(m.submissionId),
    hasMarkedPdf: !!m?.hasMarkedPdf,
  }));

  /**
   * Every quiz in the topic, from `quizzes`. The old shape's `quizData` was a
   * single object (only the topic's last quiz), so it is wrapped. Unscored
   * entries are dropped below — a quiz with score "N/A" would chart as NaN.
   */
  const quizRows = Array.isArray(r.quizzes) ? r.quizzes
    : Array.isArray(r.quizData) ? r.quizData : r.quizData ? [r.quizData] : [];
  /**
   * The headline "Quiz grade" is the latest MARKED quiz's grade — what the old
   * single-quiz report showed, since its last quiz won. The old top-level
   * `quizGrade` is used when that's all there is.
   */
  const lastGraded = [...quizRows].reverse().find((q) => na(q?.grade) != null && q.grade !== 'missing');

  const attended = r.sessionsAttended ?? 0;
  const totalSessions = r.totalSessions ?? 0;
  // Assignments with both a score and a max — the only ones that can carry a
  // percentage.
  const graded = items.filter((i) => i.score != null && i.max != null && i.max > 0);

  return {
    topicId: String(r.id ?? ''),
    topicTitle: r.topicTitle || '',
    studentName: r.studentName || '',
    semester: r.semester || '',
    sessions: { attended, total: totalSessions },
    assignments: {
      submitted: r.submittedAssignments ?? 0,
      total: r.totalAssignments ?? 0,
    },
    quizGrade: lastGraded ? lastGraded.grade : (na(r.quizGrade) ?? null),
    quizzes: quizRows
      .map((q) => ({
        label: q?.title || '',
        score: num(na(q?.scoredMark ?? q?.score)),
        max: num(q?.fullMark ?? q?.maxPoints ?? q?.max),
      }))
      .filter((q) => q.score != null)
      .map((q) => ({ ...q, max: q.max == null ? 100 : q.max })),
    items,
    attendance: (Array.isArray(r.sessions) ? r.sessions : []).map((s) => ({
      id: String(s?.sessionId ?? ''),
      date: serverDateToDmy(s?.date),
      status: SESSION_STATUS[s?.status] || 'missed',
    })),
    /**
     * Derived, because nothing else supplies them: the roster endpoint
     * (checkStudentInGroup) returns only id/name/email/banned, so a teacher
     * opening a student saw a dash for both figures even though the report
     * already held everything needed to work them out.
     * Null rather than 0 when there is nothing to average — "no marks yet" and
     * "averaged zero" are different facts.
     */
    avgPercent: graded.length
      ? Math.round(graded.reduce((t, i) => t + (i.score / i.max) * 100, 0) / graded.length)
      : null,
    attendancePercent: totalSessions > 0 ? Math.round((attended / totalSessions) * 100) : null,
  };
};

/**
 * GET /admin/createReport/:topicId — one topic, every student in the group.
 *
 * Two things make this endpoint unlike the rest:
 *   1. it answers with a BARE object, no `{ status, data }` envelope, so the
 *      caller reads it raw — and this accepts either, so a wrapped response
 *      wouldn't silently produce an empty table;
 *   2. figures it can't compute come back as the literal string 'missing',
 *      which must read as "no mark", never as a zero.
 *
 * Per student, since the multi-quiz change:
 *   assignments: [{ id, title, fullMark, scoredMark }]
 *   quizzes:     [{ id, title, fullMark, scoredMark, percentage, grade }]
 * A hand-in that hasn't been marked yet has no score, so it reads 'missing' too.
 *
 * The older shape is still read: assignments carried `status: 'done'|'missing'`
 * and there was ONE quiz, as `quizScore` / `percentage` / `grade` on the student
 * and `quizTitle` / `quizTotalScore` on the report.
 */
// The server's grade boundaries (admin_report.js / student_report.js).
const letterGrade = (pct) => (pct >= 80 ? 'A*' : pct >= 70 ? 'A' : pct >= 60 ? 'B' : pct >= 50 ? 'C' : 'U');

export const toUiClassReport = (payload) => {
  if (!payload) return null;
  const r = payload.students ? payload : (payload.data || {});
  const value = (v) => {
    if (v == null || v === '' || v === 'missing' || v === 'N/A') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // A letter grade, so it stays a string — but 'missing' is not one.
  const letter = (g) => (!g || g === 'missing' || g === 'N/A' ? null : String(g));
  // Old shape: `status`. New shape: a score means it was handed in and marked.
  const isDone = (a) => (a && 'status' in a ? a.status === 'done' : value(a?.scoredMark) != null);
  const rawStudents = Array.isArray(r.students) ? r.students : [];
  const oldQuiz = !rawStudents.some((s) => Array.isArray(s?.quizzes));

  const students = rawStudents.map((s) => {
    const work = Array.isArray(s?.assignments) ? s.assignments : [];
    const quizzes = oldQuiz
      ? [{ id: 'quiz', score: value(s?.quizScore), percent: value(s?.percentage), grade: letter(s?.grade) }]
      : (Array.isArray(s?.quizzes) ? s.quizzes : []).map((q) => ({
        id: String(q?.id ?? ''),
        score: value(q?.scoredMark),
        percent: value(q?.percentage),
        grade: letter(q?.grade),
      }));
    /**
     * One figure per student for the roster and drawer: the average percentage
     * over the quizzes they have a mark for, graded on the server's own scale.
     */
    const marked = quizzes.filter((q) => q.percent != null);
    const percent = marked.length
      ? Math.round((marked.reduce((t, q) => t + q.percent, 0) / marked.length) * 100) / 100
      : null;
    return {
      id: String(s?.id ?? ''),
      name: s?.studentName || '',
      email: s?.email || '',
      banned: !!s?.banned,
      percent,
      grade: percent == null ? null : (marked.length === 1 ? marked[0].grade : letterGrade(percent)),
      attended: value(s?.attended) ?? 0,
      submitted: work.filter(isDone).length,
      assignments: work.length,
      // Per assignment and per quiz, in the topic's order — the export's
      // one-column-per-item layout reads these.
      work: work.map((a) => ({ id: String(a?.id ?? ''), done: isDone(a), score: value(a?.scoredMark) })),
      quizzes,
    };
  });

  /**
   * The topic's assignments with their titles, for the export's column headings.
   * Every student carries the same list (the server maps the topic's assignments
   * for each one), so the longest is taken rather than trusting the first.
   */
  const longest = (Array.isArray(r.students) ? r.students : [])
    .map((s) => (Array.isArray(s?.assignments) ? s.assignments : []))
    .reduce((a, b) => (b.length > a.length ? b : a), []);
  const assignmentList = longest.map((a, i) => ({
    id: String(a?.id ?? ''),
    title: a?.title || `HW ${i + 1}`,
    max: value(a?.fullMark),
  }));

  // The same for the quizzes — or the old shape's single quiz, if it had one.
  const quizList = oldQuiz
    ? (r.quizTitle && r.quizTitle !== 'No quiz'
      ? [{ id: 'quiz', title: r.quizTitle, max: value(r.quizTotalScore) }]
      : [])
    : rawStudents
      .map((s) => (Array.isArray(s?.quizzes) ? s.quizzes : []))
      .reduce((a, b) => (b.length > a.length ? b : a), [])
      .map((q, i) => ({ id: String(q?.id ?? ''), title: q?.title || `Quiz ${i + 1}`, max: value(q?.fullMark) }));

  return {
    topicId: String(r.topicId ?? ''),
    topicTitle: r.topicName || '',
    quizList,
    // The declared count and the per-student arrays disagree in real data, so the
    // larger wins rather than under-reporting how much work the topic holds.
    assignments: Math.max(
      value(r.numberOfAssignments) ?? 0,
      ...students.map((s) => s.assignments),
      0,
    ),
    totalSessions: value(r.totalSession) ?? 0,
    assignmentList,
    students,
  };
};

/**
 * Folds the student's submissions into their coursework, so a card knows it has
 * been handed in and what it scored. Matched on the item id, because a
 * submission points at its assignment/quiz rather than the other way round.
 */
export const mergeSubmissions = (items, submissions, type) => {
  const byItem = new Map();
  submissions.filter((s) => s.type === type).forEach((s) => byItem.set(s.itemId, s));
  return items.map((item) => {
    const sub = byItem.get(item.id);
    if (!sub) return { ...item, submitted: false, markedAvailable: false };
    return {
      ...item,
      status: 'completed',
      // `submitted` is a flag, separate from `submission` (the file's URL):
      // showMySubmission lists what was handed in but carries no URLs, so the
      // card must know it's done before the PDF has been fetched. The URL is
      // loaded on demand from showSubmission/:id when the student opens it.
      submitted: true,
      submission: null,
      submissionId: sub.id,
      submittedAt: sub.submittedAt,
      grade: sub.grade,
      /**
       * Whether a MARKED copy exists — not its URL, which this endpoint doesn't
       * carry either (showMarkedSubmission/:id has it, fetched when the student
       * opens one). A recorded score is the only evidence available, and it is
       * the same test `toUiReport` reads as the server's own `hasMarkedPdf`.
       */
      markedAvailable: sub.grade != null,
    };
  });
};
